import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { searchProducts, fetchCategories, fetchProductAttributes } from '../redux/productSlice';
import CollectionCard from '../components/CollectionCard';
import { FiFilter, FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { sanitizeText } from '../utils/sanitize';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ITEMS_PER_PAGE = 12;

const SearchResults = () => {
  const query = useQuery();
  const rawSearchTerm = query.get('query') || '';
  const searchTerm = sanitizeText(rawSearchTerm, { maxLength: 80 });
  const category = query.get('category') || '';
  const minPrice = query.get('minPrice') || '';
  const maxPrice = query.get('maxPrice') || '';
  const sort = query.get('sort') || '';
  const laceSize = query.get('laceSize') || '';
  const color = query.get('color') || '';
  const rawTexture = query.get('texture') || '';
  const currentPage = parseInt(query.get('page')) || 1;

  const dispatch = useDispatch();
  const {
    searchResults,
    searchLoading,
    searchError,
    categories,
    categoriesLoading,
    attributes,
    attributesLoading,
    searchPagination
  } = useSelector(
    (state) => state.products
  );

  const normalizeTextureForDropdown = (value) => {
    if (!value) return "";
    const lower = String(value).toLowerCase().trim();

    // Keep leading letters/spaces; cut at the first number or symbol (e.g. brackets, /, -, x)
    // Then limit to at most TWO words.
    // Examples:
    // "bodywave 4"             -> "bodywave"
    // "bodywave 1b"            -> "bodywave"
    // "bodywave (4 bundles)"   -> "bodywave"
    // "water wave dd"          -> "water wave"
    // "bone straight"          -> "bone straight"
    let cutIndex = lower.length;
    for (let i = 0; i < lower.length; i++) {
      const ch = lower[i];
      if (!/[a-z\s]/.test(ch)) {
        cutIndex = i;
        break;
      }
    }

    let base = lower.slice(0, cutIndex).trim() || lower;
    const words = base.split(/\s+/).filter(Boolean);
    if (words.length > 2) {
      base = words.slice(0, 2).join(" ");
    }
    return base;
  };

  const texture = normalizeTextureForDropdown(rawTexture);

  const [filterForm, setFilterForm] = useState({
    category,
    minPrice,
    maxPrice,
    sort,
    laceSize,
    color,
    texture,
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchProductAttributes());
  }, [dispatch]);

  useEffect(() => {
    const params = {
      search: searchTerm,
      category,
      minPrice,
      maxPrice,
      sort,
      laceSize,
      color,
      texture,
      page: currentPage,
      limit: ITEMS_PER_PAGE,
    };
    
    if (searchTerm || category || minPrice || maxPrice || sort) {
      dispatch(searchProducts(params));
    }
  }, [searchTerm, category, minPrice, maxPrice, sort, laceSize, color, texture, currentPage, dispatch]);

  const handleFilterChange = (field, value) => {
    setFilterForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterSubmit = () => {
    const params = new URLSearchParams();
    
    if (searchTerm) {
      params.append('query', searchTerm);
    }
    
    Object.entries(filterForm).forEach(([key, value]) => {
      if (value && value !== "") {
        params.append(key, value);
      }
    });

    params.append('page', '1');
    params.append('limit', ITEMS_PER_PAGE.toString());

    window.location.href = `/search?${params.toString()}`;
  };

  const handleClearFilters = () => {
    setFilterForm({ category: "", minPrice: "", maxPrice: "", sort: "", laceSize: "", color: "" });
    
    const params = new URLSearchParams();
    if (searchTerm) {
      params.append('query', searchTerm);
    }
    params.append('page', '1');
    params.append('limit', ITEMS_PER_PAGE.toString());
    
    window.location.href = `/search?${params.toString()}`;
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams();
    
    if (searchTerm) params.append('query', searchTerm);
    if (category) params.append('category', category);
    if (minPrice) params.append('minPrice', minPrice);
    if (maxPrice) params.append('maxPrice', maxPrice);
    if (sort) params.append('sort', sort);
    if (laceSize) params.append('laceSize', laceSize);
    if (color) params.append('color', color);
    if (texture) params.append('texture', texture);
    params.append('page', newPage.toString());
    params.append('limit', ITEMS_PER_PAGE.toString());
    
    window.location.href = `/search?${params.toString()}`;
  };

  const renderPagination = () => {
    if (searchPagination.pages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(searchPagination.pages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="py-4 px-2">
        <div className="text-center my-3">
          <span className="text-muted small">
            Page {currentPage} of {searchPagination.pages}
            {searchPagination.total > 0 && (
              <span className="ms-2">
                • {searchPagination.total} {searchPagination.total === 1 ? 'product' : 'products'}
              </span>
            )}
          </span>
        </div>

        <nav aria-label="Product pagination">
          <ul className="pagination justify-content-center mb-0">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <FiChevronLeft />
              </button>
            </li>

            {pages.map(page => (
              <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              </li>
            ))}

            <li className={`page-item ${currentPage === searchPagination.pages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === searchPagination.pages}
              >
                <FiChevronRight />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  const textureOptions = (() => {
    const raw = attributes?.textures || [];
    const map = new Map();
    raw.forEach((t) => {
      const original = String(t || "").trim();
      if (!original) return;
      const key = normalizeTextureForDropdown(original);
      if (!key) return;
      if (!map.has(key)) {
        const display = key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        map.set(key, { key, display });
      }
    });
    return Array.from(map.values());
  })();

  return (
    <div className="container py-4">
      <div className="mb-4">
        <div className="d-lg-none mb-3">
          <button
            className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-between"
            onClick={() => setShowFilters(!showFilters)}
          >
            <span>
              Filter & Sort Products
            </span>
            {showFilters ? <FiChevronUp /> : <FiChevronDown />}
          </button>
        </div>

        <div className={`bg-light rounded shadow-sm ${showFilters ? 'd-block' : 'd-none d-lg-block'}`}>
          <div className="p-3">
            <div className="d-none d-lg-flex align-items-center mb-3">
              <FiFilter className="me-2" />
              <h6 className="mb-0">Filter & Sort Products</h6>
            </div>
            
            <div className="row g-3">
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label small">Category</label>
                <select
                  className="form-select form-select-sm"
                  value={filterForm.category}
                  onChange={(e) => handleFilterChange("category", e.target.value)}
                  disabled={categoriesLoading}
                >
                  <option value="" disabled>All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div className="col-6 col-md-3 col-lg-2">
                <label className="form-label small">Min Price (£)</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={filterForm.minPrice}
                  onChange={(e) => handleFilterChange("minPrice", e.target.value)}
                  min="0"
                  placeholder="0"
                />
              </div>
              
              <div className="col-6 col-md-3 col-lg-2">
                <label className="form-label small">Max Price (£)</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={filterForm.maxPrice}
                  onChange={(e) => handleFilterChange("maxPrice", e.target.value)}
                  min="0"
                  placeholder="Any"
                />
              </div>
              
              <div className="col-6 col-md-3 col-lg-2">
                <label className="form-label small">Lace Size</label>
                <select
                  className="form-select form-select-sm"
                  value={filterForm.laceSize}
                  onChange={(e) => handleFilterChange("laceSize", e.target.value)}
                  disabled={attributesLoading}
                >
                  <option value="">All</option>
                  {attributes.laceSizes.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-3 col-lg-2">
                <label className="form-label small">Color</label>
                <select
                  className="form-select form-select-sm"
                  value={filterForm.color}
                  onChange={(e) => handleFilterChange("color", e.target.value)}
                  disabled={attributesLoading}
                >
                  <option value="">All</option>
                  {attributes.colors.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-3 col-lg-2">
                <label className="form-label small">Texture</label>
                <select
                  className="form-select form-select-sm"
                  value={filterForm.texture || ""}
                  onChange={(e) => handleFilterChange("texture", e.target.value)}
                  disabled={attributesLoading}
                >
                  <option value="">All</option>
                  {textureOptions.map((t) => (
                    <option key={t.key} value={t.key}>{t.display}</option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label small">Sort By</label>
                <select
                  className="form-select form-select-sm"
                  value={filterForm.sort}
                  onChange={(e) => handleFilterChange("sort", e.target.value)}
                >
                  <option value="">Newest First</option>
                  <option value="priceAsc">Price: Low to High</option>
                  <option value="priceDesc">Price: High to Low</option>
                  <option value="popular">Most Popular</option>
                </select>
              </div>
              
              <div className="col-12 col-md-6 col-lg-2 d-flex align-items-end gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm flex-fill"
                  onClick={handleClearFilters}
                >
                  Clear
                </button>
                <button
                  className="btn btn-dark btn-sm flex-fill"
                  onClick={handleFilterSubmit}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {searchLoading && (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      
      {searchError && (
        <div className="alert alert-danger" role="alert">
          Error: {searchError}
        </div>
      )}
      
      {!searchLoading && !searchError && (
        <>
          {searchResults.length > 0 ? (
            <>
              <div className="row gx-3 my-4">
                {searchResults.map((product) => (
                  <CollectionCard key={product._id} data={product} compact={true} />
                ))}
              </div>
              
              {renderPagination()}
            </>
          ) : (
            <div className="text-center py-5">
              <p className="text-muted">No products found for "<strong>{searchTerm}</strong>".</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SearchResults;
